import React, { useState } from 'react';
import BottomSheet from './BottomSheet';
import ShareCardSheet from './ShareCardSheet';
import { totalXpForLevel, xpProgressInLevel } from '../lib/xpUtils';
import { formatVolume, formatDuration } from '../lib/workoutSummary';
import { RARITY } from '../lib/badgeDefinitions';

/**
 * Popup di fine allenamento. Compare SEMPRE al completamento:
 * - statistiche del riepilogo (summary) se disponibili;
 * - XP/livello SOLO se l'assegnazione è riuscita (xpResult può essere null);
 * - eventuali badge appena sbloccati (newBadges).
 * I dati arrivano già calcolati (lib/workoutSummary): la stessa struttura
 * alimenterà la futura card condivisibile.
 */
function WorkoutCompletePopup({ xpResult, summary, newBadges = [], workoutName = '', athleteId = null, onClose }) {
  // Condivisione: totalmente facoltativa, si apre SOLO dal bottone secondario
  // (il workout è già salvato quando questo popup esiste).
  const [showShare, setShowShare] = useState(false);
  const canShareCard = !!summary && !!athleteId;
  const hasXp = !!xpResult && Number.isFinite(Number(xpResult.totalXp));
  const level = hasXp ? (xpResult.currentLevel || 1) : 1;
  const leveledUp = hasXp && xpResult.leveledUp;
  const progress = hasXp ? xpProgressInLevel(xpResult.totalXp) : 0;
  const xpInLevel = hasXp ? xpResult.totalXp - totalXpForLevel(level) : 0;
  const xpNeeded = hasXp ? totalXpForLevel(level + 1) - totalXpForLevel(level) : 0;

  const volume = summary ? formatVolume(summary.totalVolumeKg) : null;

  return (
    <BottomSheet
      onClose={onClose}
      zIndexClass="z-[1100]"
      footer={
        <>
          <button
            onClick={onClose}
            className="w-full py-4 bg-primary-600 text-white font-semibold rounded-2xl text-base"
          >
            Continua
          </button>
          {canShareCard && (
            <button
              type="button"
              onClick={() => setShowShare(true)}
              className="w-full py-3 rounded-2xl font-semibold text-sm bg-white text-primary-700 border border-primary-200 active:bg-primary-50"
            >
              📤 Condividi la tua card
            </button>
          )}
        </>
      }
    >
      <div className="px-6 pt-3 pb-4 space-y-5">
        {/* Header */}
        <div className="text-center">
          <div className="text-5xl mb-3">{leveledUp ? '🎊' : '🏋️'}</div>
          <h2 className="text-2xl font-bold text-gray-900">
            {leveledUp ? `Livello ${level} raggiunto!` : 'Allenamento completato!'}
          </h2>
        </div>

        {/* Statistiche della sessione */}
        {summary && (
          <div className="grid grid-cols-2 gap-2.5">
            <StatTile icon="⏱️" value={formatDuration(summary.durationMinutes)} label="Durata" />
            <StatTile icon="📦" value={volume || '—'} label="Volume sollevato" />
            <StatTile icon="🏋️" value={summary.exercisesCount} label={summary.exercisesCount === 1 ? 'Esercizio' : 'Esercizi'} />
            <StatTile icon="✅" value={summary.setsCount} label={summary.setsCount === 1 ? 'Serie' : 'Serie totali'} />
          </div>
        )}

        {/* Nuovi badge sbloccati */}
        {newBadges.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-400 text-center">
              {newBadges.length === 1 ? 'Nuovo badge sbloccato' : 'Nuovi badge sbloccati'} 🎉
            </p>
            {newBadges.map((b) => {
              const rarity = RARITY[b.rarity] || RARITY.comune;
              return (
                <div key={b.key} className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl p-3 shadow-sm">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ring-1 bg-gradient-to-b shrink-0 ${rarity.glow} ${rarity.ring}`}>
                    <span className="text-2xl">{b.icon}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 text-sm leading-tight">{b.name}</p>
                    <p className="text-xs text-gray-500 leading-snug">{b.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* XP gained pill (solo se gli XP sono stati assegnati) */}
        {hasXp && xpResult.xpGained > 0 && (
          <div className="flex items-center justify-center gap-2 bg-yellow-50 border border-yellow-200 rounded-2xl py-3">
            <span className="text-2xl">⭐</span>
            <span className="text-xl font-bold text-yellow-700">+{xpResult.xpGained} XP guadagnati</span>
          </div>
        )}

        {/* Level card (solo se gli XP sono disponibili) */}
        {hasXp && (
          <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
                  {level}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm leading-tight">Livello {level}</p>
                  <p className="text-xs text-gray-500">{xpInLevel} / {xpNeeded} XP</p>
                </div>
              </div>
              <span className="text-xs text-gray-400 font-medium">Liv. {level + 1} →</span>
            </div>

            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-700"
                style={{ width: `${Math.min(progress * 100, 100).toFixed(1)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {showShare && (
        <ShareCardSheet
          summary={summary}
          xpResult={xpResult}
          newBadges={newBadges}
          workoutName={workoutName}
          athleteId={athleteId}
          onClose={() => setShowShare(false)}
        />
      )}
    </BottomSheet>
  );
}

function StatTile({ icon, value, label }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-3 text-center">
      <p className="text-lg font-bold text-gray-900 leading-tight">
        <span className="mr-1">{icon}</span>{value}
      </p>
      <p className="text-[11px] text-gray-400 mt-0.5">{label}</p>
    </div>
  );
}

export default WorkoutCompletePopup;
